/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { 
  Settings,
  Loader2,
  Save,
  RefreshCw,
  FolderOpen,
  Database,
  Zap,
  Sparkles,
  AlertCircle,
  Clock,
  Play,
  CheckCircle,
} from 'lucide-react';

// Services - Using query function factories for simple endpoints
import {
  // Query keys for cache management
  PLAYBOOK_QUERY_KEYS,
  SKILLS_QUERY_KEYS,
  MCP_QUERY_KEYS,
  VPS_INDEX_QUERY_KEYS,
  // Query function factories
  playbookStatsQueryFn,
  skillsStatsQueryFn,
  mcpStatsQueryFn,
  semanticSearchStatusQueryFn,
  vpsIndexStatusQueryFn,
  // Service functions for complex operations
  getContextConfig,
  updateContextConfig,
  getAssemblyStrategies,
  updateIndexSettings,
  triggerIndexRebuild,
} from '@/services/skillshop';
import type { AssemblyStrategy, ContextConfig, IndexType } from '@/services/skillshop';

// Shared components
import { IndexStatus } from '../shared/IndexStatus';

export function SettingsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch stats - using query function factories
  const { data: playbookStats, isLoading: isLoadingPlaybookStats } = useQuery({
    queryKey: PLAYBOOK_QUERY_KEYS.stats,
    queryFn: playbookStatsQueryFn,
    staleTime: 5 * 60 * 1000,
  });

  const { data: skillsStats, isLoading: isLoadingSkillsStats } = useQuery({
    queryKey: SKILLS_QUERY_KEYS.stats,
    queryFn: skillsStatsQueryFn,
    staleTime: 5 * 60 * 1000,
  });

  const { data: mcpStats, isLoading: isLoadingMcpStats } = useQuery({
    queryKey: MCP_QUERY_KEYS.stats,
    queryFn: mcpStatsQueryFn,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch VPS index status - for auto-rebuild settings and status
  const { data: indexStatus, isLoading: isLoadingIndexStatus } = useQuery({
    queryKey: VPS_INDEX_QUERY_KEYS.status,
    queryFn: vpsIndexStatusQueryFn,
    staleTime: 5 * 1000, // Refresh every 5 seconds for status updates
    refetchInterval: 5000, // Auto-refresh while on page
  });

  // Fetch context config - still uses service function (has transformation)
  const { data: contextConfig, isLoading: isLoadingConfig } = useQuery({
    queryKey: ['context-config'],
    queryFn: getContextConfig,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch assembly strategies - still uses service function (has transformation)
  const { data: strategiesData } = useQuery({
    queryKey: ['assembly-strategies'],
    queryFn: getAssemblyStrategies,
    staleTime: 30 * 60 * 1000,
  });

  // Fetch semantic search status - using query function factory
  const { data: semanticStatus } = useQuery({
    queryKey: PLAYBOOK_QUERY_KEYS.semanticSearchStatus,
    queryFn: semanticSearchStatusQueryFn,
    staleTime: 60 * 1000,
  });

  // Local config state for editing
  const [localConfig, setLocalConfig] = useState<Partial<ContextConfig>>({});
  
  // R7: Track if there are unsaved changes
  const hasUnsavedChanges = Object.keys(localConfig).length > 0;

  // R7: Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        // Modern browsers ignore custom messages, but returnValue must be set
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: (config: Partial<ContextConfig>) => updateContextConfig(config),
    onSuccess: () => {
      // R7: Clear local changes after successful save
      setLocalConfig({});
      queryClient.invalidateQueries({ queryKey: ['context-config'] });
      toast({
        title: 'Settings Saved',
        description: 'Context assembly configuration has been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save settings.',
        variant: 'destructive',
      });
    },
  });

  // Update auto-rebuild settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: updateIndexSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VPS_INDEX_QUERY_KEYS.status });
      toast({
        title: 'Settings Saved',
        description: 'Auto-rebuild settings have been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update settings.',
        variant: 'destructive',
      });
    },
  });

  // Trigger manual rebuild mutation
  const triggerRebuildMutation = useMutation({
    mutationFn: (types?: IndexType[]) => triggerIndexRebuild(types),
    onSuccess: (_, types) => {
      queryClient.invalidateQueries({ queryKey: VPS_INDEX_QUERY_KEYS.status });
      queryClient.invalidateQueries({ queryKey: PLAYBOOK_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: MCP_QUERY_KEYS.all });
      const typesStr = types?.join(', ') || 'all indexes';
      toast({
        title: 'Rebuild Triggered',
        description: `Rebuilding ${typesStr}...`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to trigger rebuild.',
        variant: 'destructive',
      });
    },
  });

  // Handle config changes with nested path support
  const handleConfigChange = (path: string, value: any) => {
    setLocalConfig(prev => {
      const newConfig = { ...prev };
      // Handle nested paths like 'strategies.defaultStrategy', 'defaults.maxPlaybooks'
      const parts = path.split('.');
      if (parts.length === 1) {
        (newConfig as any)[path] = value;
      } else if (parts.length === 2) {
        const [parent, child] = parts;
        (newConfig as any)[parent] = { 
          ...((newConfig as any)[parent] || {}),
          [child]: value 
        };
      }
      return newConfig;
    });
  };

  // Save config
  const handleSaveConfig = () => {
    if (Object.keys(localConfig).length > 0) {
      updateConfigMutation.mutate(localConfig);
    }
  };

  // Get effective config value with nested path support
  const getConfigValue = (path: string): any => {
    const parts = path.split('.');
    let localValue: any = localConfig;
    let configValue: any = contextConfig;
    
    for (const part of parts) {
      localValue = localValue?.[part];
      configValue = configValue?.[part];
    }
    
    return localValue ?? configValue;
  };

  const isLoading = isLoadingPlaybookStats || isLoadingSkillsStats || isLoadingMcpStats || isLoadingConfig || isLoadingIndexStatus;

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Context Assembly Configuration */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Zap className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Context Assembly</h3>
          {/* R7: Visual indicator for unsaved changes */}
          {hasUnsavedChanges && (
            <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">
              <AlertCircle className="h-3 w-3 mr-1" />
              Unsaved changes
            </Badge>
          )}
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Default Strategy</label>
            <Select
              value={getConfigValue('strategies.defaultStrategy') || 'simple'}
              onValueChange={(v) => handleConfigChange('strategies.defaultStrategy', v)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {strategiesData?.strategies.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                    {s.recommended && <Badge variant="secondary" className="ml-2 text-xs">Recommended</Badge>}
                  </SelectItem>
                )) || (
                  <>
                    <SelectItem value="simple">Simple</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                    <SelectItem value="rlm">RLM</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Strategy used for assembling context for AI agents.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Max Playbooks</label>
              <Input
                type="number"
                min={1}
                max={20}
                value={getConfigValue('defaults.maxPlaybooks') ?? 5}
                onChange={(e) => handleConfigChange('defaults.maxPlaybooks', parseInt(e.target.value))}
                className="w-24"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Max Skills</label>
              <Input
                type="number"
                min={1}
                max={10}
                value={getConfigValue('defaults.maxSkills') ?? 3}
                onChange={(e) => handleConfigChange('defaults.maxSkills', parseInt(e.target.value))}
                className="w-24"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Min Relevance Score</label>
            <Input
              type="number"
              min={0}
              max={1}
              step={0.1}
              value={getConfigValue('defaults.minScore') ?? 0.3}
              onChange={(e) => handleConfigChange('defaults.minScore', parseFloat(e.target.value))}
              className="w-24"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Minimum score (0-1) for content to be included in context.
            </p>
          </div>

          {/* R7: Button disabled when no changes, highlighted when there are changes */}
          <Button
            onClick={handleSaveConfig}
            disabled={!hasUnsavedChanges || updateConfigMutation.isPending}
            variant={hasUnsavedChanges ? 'default' : 'outline'}
          >
            {updateConfigMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {hasUnsavedChanges ? 'Save Configuration' : 'No Changes'}
          </Button>
        </div>
      </section>

      {/* Semantic Search Status */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Semantic Search</h3>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <div className={`h-3 w-3 rounded-full ${semanticStatus?.available ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-sm font-medium">
                {semanticStatus?.available ? 'Available' : 'Unavailable'}
              </span>
            </div>
            {semanticStatus?.indexedDirectoryCount !== undefined && (
              <Badge variant="outline">
                {semanticStatus.indexedDirectoryCount} indexed directories
              </Badge>
            )}
          </div>
          {semanticStatus?.directories && semanticStatus.directories.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2">Indexed directories:</p>
              <ul className="text-xs space-y-1">
                {semanticStatus.directories.slice(0, 5).map((dir, idx) => (
                  <li key={idx} className="font-mono text-muted-foreground truncate">
                    {dir.displayName || dir.path}
                  </li>
                ))}
                {semanticStatus.directories.length > 5 && (
                  <li className="text-muted-foreground">
                    +{semanticStatus.directories.length - 5} more...
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Auto-Rebuild Settings */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <RefreshCw className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Auto-Rebuild Settings</h3>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-rebuild">Auto-Rebuild Enabled</Label>
              <p className="text-xs text-muted-foreground">
                Automatically rebuild indexes at the specified interval
              </p>
            </div>
            <Switch
              id="auto-rebuild"
              checked={indexStatus?.settings.autoRebuildEnabled ?? true}
              onCheckedChange={(checked) => updateSettingsMutation.mutate({ autoRebuildEnabled: checked })}
              disabled={updateSettingsMutation.isPending || isLoadingIndexStatus}
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="space-y-1">
              <Label htmlFor="rebuild-interval">Rebuild Interval</Label>
              <Select
                value={String(indexStatus?.settings.rebuildIntervalMinutes ?? 5)}
                onValueChange={(v) => updateSettingsMutation.mutate({ rebuildIntervalMinutes: parseInt(v) })}
                disabled={!indexStatus?.settings.autoRebuildEnabled || updateSettingsMutation.isPending}
              >
                <SelectTrigger className="w-[140px]" id="rebuild-interval">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 minute</SelectItem>
                  <SelectItem value="2">2 minutes</SelectItem>
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="10">10 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {indexStatus?.isRunning && indexStatus?.nextScheduledBuild && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Next rebuild: {new Date(indexStatus.nextScheduledBuild).toLocaleTimeString()}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => triggerRebuildMutation.mutate(undefined)}
              disabled={triggerRebuildMutation.isPending || indexStatus?.isBuilding}
            >
              {triggerRebuildMutation.isPending || indexStatus?.isBuilding ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Rebuild All Indexes Now
            </Button>
            {indexStatus?.isBuilding && (
              <Badge variant="secondary" className="animate-pulse">
                Building...
              </Badge>
            )}
          </div>
        </div>
      </section>

      {/* Index Status */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Database className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Index Status</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Playbooks Index */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">Playbooks</h4>
              <Badge variant="outline">{indexStatus?.indexes.playbooks?.itemCount ?? playbookStats?.index?.playbooksCount ?? 0} indexed</Badge>
            </div>
            {indexStatus?.indexes.playbooks && (
              <div className="mb-3">
                <IndexStatus status={indexStatus.indexes.playbooks} />
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => triggerRebuildMutation.mutate(['playbooks'])}
              disabled={triggerRebuildMutation.isPending || indexStatus?.indexes.playbooks?.status === 'building'}
            >
              {indexStatus?.indexes.playbooks?.status === 'building' ? (
                <Loader2 className="h-3 w-3 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-2" />
              )}
              Rebuild
            </Button>
          </div>

          {/* Skills Index */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">Skills</h4>
              <Badge variant="outline">{indexStatus?.indexes.skills?.itemCount ?? skillsStats?.index?.skillsCount ?? 0} indexed</Badge>
            </div>
            {indexStatus?.indexes.skills && (
              <div className="mb-3">
                <IndexStatus status={indexStatus.indexes.skills} />
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => triggerRebuildMutation.mutate(['skills'])}
              disabled={triggerRebuildMutation.isPending || indexStatus?.indexes.skills?.status === 'building'}
            >
              {indexStatus?.indexes.skills?.status === 'building' ? (
                <Loader2 className="h-3 w-3 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-2" />
              )}
              Rebuild
            </Button>
          </div>

          {/* MCP Registry */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">MCP Servers</h4>
              <Badge variant="outline">{indexStatus?.indexes.mcp?.itemCount ?? mcpStats?.totalServers ?? 0} registered</Badge>
            </div>
            {indexStatus?.indexes.mcp && (
              <div className="mb-3">
                <IndexStatus status={indexStatus.indexes.mcp} />
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => triggerRebuildMutation.mutate(['mcp'])}
              disabled={triggerRebuildMutation.isPending || indexStatus?.indexes.mcp?.status === 'building'}
            >
              {indexStatus?.indexes.mcp?.status === 'building' ? (
                <Loader2 className="h-3 w-3 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-2" />
              )}
              Rebuild
            </Button>
          </div>
        </div>
      </section>

      {/* Token Budgets */}
      {playbookStats?.config && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Settings className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">Token Budgets</h3>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-4">
              These values are configured in the server environment and control token limits for context assembly.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {playbookStats?.config?.tokenBudgets && Object.entries(playbookStats.config.tokenBudgets).map(([key, value]) => (
                <div key={key}>
                  <dt className="text-xs text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</dt>
                  <dd className="text-sm font-medium">{value.toLocaleString()} tokens</dd>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
